import type { AssessmentState } from './crs.ts';
import type { PathwayAnswers } from './pathway-types.ts';
import { INITIAL_PATHWAY_ANSWERS, isFstTradeNoc } from './pathways.ts';

export type ProfileReuseKind = 'fsw' | 'cec' | 'fst';
export type ProfileReuseConfirmations = Partial<Record<ProfileReuseKind, {
  source: string;
  fields: Partial<PathwayAnswers>;
}>>;

export interface ProfileReuseCandidates {
  fswTotalMonths: string | null;
  cecMonths: string | null;
  cecTeer: string | null;
  hasCanadianTradeCertificate: boolean;
}

function months(value: string, maximum: number): number | null {
  if (value.trim() === '') return null;
  const number = Number(value);
  return Number.isSafeInteger(number) && number >= 0 && number <= maximum ? number : null;
}

function skilledCanadianTeer(profile: AssessmentState): boolean {
  return Number.isInteger(profile.canadianTeer) && profile.canadianTeer >= 0 && profile.canadianTeer <= 3;
}

export function getProfileReuseCandidates(profile: AssessmentState): ProfileReuseCandidates {
  const canadian = months(profile.canadianMonths, 120);
  const foreign = months(profile.foreignMonths, 120);
  const fsw = canadian !== null && foreign !== null && canadian + foreign <= 120 &&
    (canadian === 0 || skilledCanadianTeer(profile)) ? String(canadian + foreign) : null;
  const cec = months(profile.canadianMonths, 36);
  return {
    fswTotalMonths: fsw,
    cecMonths: cec !== null && skilledCanadianTeer(profile) ? String(cec) : null,
    cecTeer: skilledCanadianTeer(profile) ? String(profile.canadianTeer) : null,
    hasCanadianTradeCertificate: profile.hasTradeCertificate,
  };
}

/** Binds an explicit confirmation to the source facts; it is never itself consent. */
export function getProfileReuseSource(kind: ProfileReuseKind, profile: AssessmentState, answers: PathwayAnswers): string {
  return JSON.stringify(kind === 'fsw'
    ? [profile.canadianMonths, profile.foreignMonths, String(profile.canadianTeer)]
    : kind === 'cec' ? [profile.canadianMonths, String(profile.canadianTeer)]
      : [profile.hasTradeCertificate, answers.fstNoc]);
}

export const PROFILE_REUSE_FIELDS: Record<ProfileReuseKind, (keyof PathwayAnswers)[]> = {
  fsw: ['fswTotalMonths', 'fswWithin10Years', 'fswDuties', 'fswHoursCapped', 'fswCanadianWorkAuthorized'],
  cec: ['cecMonths', 'cecTeer', 'cecWithin3Years'],
  fst: ['fstCertificate'],
};

/**
 * Source edits revoke confirmed reuse rather than silently qualifying new facts.
 * The UI removes a kind's metadata when its answers are deliberately edited by
 * hand. Independent fields are never overwritten; changed manual values survive.
 */
export function invalidateProfileReuse(profile: AssessmentState, answers: PathwayAnswers, confirmations: ProfileReuseConfirmations): {
  answers: PathwayAnswers; confirmations: ProfileReuseConfirmations;
} {
  const nextAnswers = { ...answers };
  const nextConfirmations = { ...confirmations };
  for (const kind of ['fsw', 'cec', 'fst'] as const) {
    const confirmed = confirmations[kind];
    if (!confirmed || confirmed.source === getProfileReuseSource(kind, profile, answers)) continue;
    for (const field of PROFILE_REUSE_FIELDS[kind]) {
      if (Object.hasOwn(confirmed.fields, field) && nextAnswers[field] === confirmed.fields[field])
        Object.assign(nextAnswers, { [field]: INITIAL_PATHWAY_ANSWERS[field] });
    }
    delete nextConfirmations[kind];
  }
  return { answers: nextAnswers, confirmations: nextConfirmations };
}

/** Call only after a user confirms the complete FSW work-scope statement. */
export function confirmFswWorkReuse(profile: AssessmentState, confirmed: boolean | null): Partial<PathwayAnswers> {
  const total = getProfileReuseCandidates(profile).fswTotalMonths;
  if (confirmed !== true || total === null) return {};
  return {
    fswTotalMonths: total, fswWithin10Years: true,
    // fswPaid also asserts no gaps in the separately claimed continuous period.
    // The shared total does not establish that period, so this answer stays manual.
    fswDuties: true, fswHoursCapped: true, fswCanadianWorkAuthorized: true,
  };
}

/** The same CRS Canadian work is explicitly confirmed to fit in the last three years. */
export function confirmCecWorkReuse(profile: AssessmentState, confirmed: boolean | null): Partial<PathwayAnswers> {
  const candidate = getProfileReuseCandidates(profile);
  if (confirmed !== true || candidate.cecMonths === null || candidate.cecTeer === null) return {};
  return { cecMonths: candidate.cecMonths, cecTeer: candidate.cecTeer, cecWithin3Years: true };
}

/** Confirms the stated Canadian certificate covers this exact FST trade and permits practice. */
export function confirmFstCertificateReuse(profile: AssessmentState, answers: PathwayAnswers, confirmed: boolean | null): Partial<PathwayAnswers> {
  return confirmed === true && profile.hasTradeCertificate && isFstTradeNoc(answers.fstNoc)
    ? { fstCertificate: true } : {};
}
